import React, { useCallback, useEffect, useMemo, useState } from 'react';
import http from '../api/http';
import WideModal from '../components/WideModal';

const toDateRangeLabel = (plan) => {
  const start = plan?.training_start_date ? String(plan.training_start_date).slice(0, 10) : '';
  const end = plan?.training_end_date ? String(plan.training_end_date).slice(0, 10) : '';
  if (start && end) return `${start} ~ ${end}`;
  if (start) return start;
  if (end) return end;
  return '—';
};

const normalizeNumberInput = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = Number(value);
  return Number.isFinite(num) ? num : '';
};

export default function OnJobTrainingLedgerTab({ addToast }) {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const [activePlanId, setActivePlanId] = useState('');
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessionsEmployee, setSessionsEmployee] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsSaving, setSessionsSaving] = useState(false);
  const [sessions, setSessions] = useState([]);

  const [sessionSignOpen, setSessionSignOpen] = useState(false);
  const [sessionSigningRow, setSessionSigningRow] = useState(null);
  const [sessionSignSaving, setSessionSignSaving] = useState(false);

  const sessionCanvasRef = React.useRef(null);
  const sessionDrawingRef = React.useRef({ drawing: false, lastX: 0, lastY: 0 });

  const sectionTitleStyle = { fontSize: '1.1rem', fontWeight: 600, margin: '0 0 .75rem', color: 'var(--text-primary)' };
  const hintStyle = { fontSize: 12, color: 'var(--text-secondary)', marginTop: '.75rem', lineHeight: 1.5 };
  const actionsBarStyle = { display: 'flex', gap: '.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' };
  const btn = (variant = 'primary') => ({
    padding: '.55rem 1rem',
    fontSize: 13,
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 500,
    border: '1px solid',
    background: variant === 'primary' ? 'var(--brand-primary)' : 'var(--bg-tertiary)',
    color: variant === 'primary' ? '#fff' : 'var(--text-primary)',
    borderColor: variant === 'primary' ? 'var(--brand-primary)' : 'var(--border-color)',
    transition: 'background .2s, color .2s, border-color .2s'
  });

  const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid var(--border-color)', borderRadius: 8 };

  const statusLabel = (value) => {
    if (value === 'completed') return '已完成';
    return '未完成';
  };

  const isNumericId = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'number') return Number.isFinite(value) && value > 0;
    const str = String(value);
    return /^\d+$/.test(str) && Number(str) > 0;
  };

  const fitSessionCanvas = useCallback(() => {
    const canvas = sessionCanvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const cssWidth = parent ? parent.clientWidth : 320;
    const cssHeight = 220;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(cssWidth * ratio));
    canvas.height = Math.max(1, Math.floor(cssHeight * ratio));
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cssWidth, cssHeight);
  }, []);

  const clearSessionCanvas = () => {
    const canvas = sessionCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const cssWidth = Number(String(canvas.style.width || '').replace('px', '')) || 320;
    const cssHeight = Number(String(canvas.style.height || '').replace('px', '')) || 220;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cssWidth, cssHeight);
  };

  const getSessionPoint = (event) => {
    const canvas = sessionCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const isTouch = event.touches && event.touches[0];
    const clientX = isTouch ? event.touches[0].clientX : event.clientX;
    const clientY = isTouch ? event.touches[0].clientY : event.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startSessionDraw = (e) => {
    const canvas = sessionCanvasRef.current;
    if (!canvas) return;
    e.preventDefault?.();
    const p = getSessionPoint(e);
    sessionDrawingRef.current = { drawing: true, lastX: p.x, lastY: p.y };
  };

  const moveSessionDraw = (e) => {
    const canvas = sessionCanvasRef.current;
    if (!canvas) return;
    if (!sessionDrawingRef.current.drawing) return;
    e.preventDefault?.();
    const p = getSessionPoint(e);
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(sessionDrawingRef.current.lastX, sessionDrawingRef.current.lastY);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    sessionDrawingRef.current.lastX = p.x;
    sessionDrawingRef.current.lastY = p.y;
  };

  const endSessionDraw = (e) => {
    e.preventDefault?.();
    sessionDrawingRef.current.drawing = false;
  };

  const openSessionSign = (s) => {
    if (!isNumericId(s?.id)) {
      addToast?.('请先保存分次记录后再签名', 'warning');
      return;
    }
    setSessionSigningRow(s);
    setSessionSignOpen(true);
    setTimeout(() => fitSessionCanvas(), 0);
  };

  const saveSessionSignature = async () => {
    if (!activePlanId || !sessionsEmployee?.employee_id || !isNumericId(sessionSigningRow?.id)) return;
    const canvas = sessionCanvasRef.current;
    if (!canvas) return;
    setSessionSignSaving(true);
    try {
      const dataUrl = canvas.toDataURL('image/png');
      await http.put(
        `/education-training/training-plans/${activePlanId}/participants/${sessionsEmployee.employee_id}/sessions/${sessionSigningRow.id}/signature`,
        { signature_data: dataUrl }
      );
      addToast?.('场次签名已保存', 'success');
      setSessionSignOpen(false);
      setSessionSigningRow(null);
      // 刷新 sessions & participants，以反馈到台账列表
      await openSessionsModal(sessionsEmployee);
      await fetchParticipants(activePlanId);
    } catch (err) {
      console.error('[onjob-ledger-tab] save session signature failed:', err);
      addToast?.(err.response?.data?.message || '保存场次签名失败', 'error');
    } finally {
      setSessionSignSaving(false);
    }
  };

  const openSessionsModal = async (row) => {
    if (!activePlanId || !row?.employee_id) return;
    setSessionsEmployee(row);
    setSessionsOpen(true);
    setSessionsLoading(true);
    try {
      const res = await http.get(`/education-training/training-plans/${activePlanId}/participants/${row.employee_id}/sessions`);
      const list = Array.isArray(res.data) ? res.data : [];
      setSessions(
        list.map((s) => ({
          id: s.id,
          session_date: s.session_date ? String(s.session_date).slice(0, 10) : '',
          hours: s.hours ?? '',
          remark: s.remark || '',
          signature_data: s.signature_data || null,
          signer_name: s.signer_name || null,
          signed_at: s.signed_at || null
        }))
      );
    } catch (err) {
      console.error('[onjob-ledger-tab] fetch sessions failed:', err);
      addToast?.(err.response?.data?.message || '获取分次学时记录失败', 'error');
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const addSessionRow = () => {
    const today = new Date().toISOString().slice(0, 10);
    setSessions((prev) => prev.concat([{ id: `new_${Date.now()}`, session_date: today, hours: '', remark: '' }]));
  };

  const removeSessionRow = (id) => {
    setSessions((prev) => prev.filter((s) => s.id !== id));
  };

  const updateSessionRow = (id, field, value) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const sessionsTotalHours = useMemo(() => {
    return sessions.reduce((acc, s) => acc + (Number(s.hours) || 0), 0);
  }, [sessions]);

  const saveSessions = async () => {
    if (!activePlanId || !sessionsEmployee?.employee_id) return;
    setSessionsSaving(true);
    try {
      const payload = {
        sessions: sessions
          .map((s) => ({
            id: isNumericId(s.id) ? Number(s.id) : null,
            session_date: s.session_date || null,
            hours: s.hours === '' ? null : Number(s.hours),
            remark: s.remark || null
          }))
          .filter((s) => Boolean(s.session_date) && s.hours !== null)
      };
      await http.put(
        `/education-training/training-plans/${activePlanId}/participants/${sessionsEmployee.employee_id}/sessions`,
        payload
      );
      addToast?.('分次学时已保存', 'success');
      setSessionsOpen(false);
      setSessionsEmployee(null);
      setSessions([]);
      await fetchParticipants(activePlanId);
    } catch (err) {
      console.error('[onjob-ledger-tab] save sessions failed:', err);
      addToast?.(err.response?.data?.message || '保存分次学时失败', 'error');
    } finally {
      setSessionsSaving(false);
    }
  };

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const res = await http.get('/education-training/training-plans');
      const list = Array.isArray(res.data) ? res.data : [];
      setPlans(list);
      if (!activePlanId && list.length > 0) {
        setActivePlanId(String(list[0].id));
      }
    } catch (err) {
      console.error('[onjob-ledger-tab] fetch plans failed:', err);
      addToast?.(err.response?.data?.message || '获取培训计划失败', 'error');
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [activePlanId, addToast]);

  const fetchParticipants = useCallback(async (planId) => {
    if (!planId) {
      setParticipants([]);
      return;
    }
    setParticipantsLoading(true);
    try {
      const res = await http.get(`/education-training/training-plans/${planId}/participants`);
      const rows = Array.isArray(res.data) ? res.data : [];
      setParticipants(
        rows.map((r) => ({
          ...r,
          completed_hours: normalizeNumberInput(r.completed_hours),
          score: normalizeNumberInput(r.score),
          completed_at: r.completed_at ? String(r.completed_at).slice(0, 10) : ''
        }))
      );
    } catch (err) {
      console.error('[onjob-ledger-tab] fetch participants failed:', err);
      addToast?.(err.response?.data?.message || '获取参培人员失败', 'error');
      setParticipants([]);
    } finally {
      setParticipantsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    if (!activePlanId) return;
    fetchParticipants(activePlanId);
  }, [activePlanId, fetchParticipants]);

  const activePlan = useMemo(() => plans.find((p) => String(p.id) === String(activePlanId)) || null, [activePlanId, plans]);

  const handleRowChange = (rowId, field, value) => {
    setParticipants((prev) => prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r)));
  };

  const markAllCompletedToday = () => {
    const today = new Date().toISOString().slice(0, 10);
    setParticipants((prev) =>
      prev.map((r) => ({
        ...r,
        status: 'completed',
        completed_at: r.completed_at || today
      }))
    );
  };

  const save = async () => {
    if (!activePlanId) return;
    setSaving(true);
    try {
      const payload = {
        participants: participants.map((r) => ({
          employee_id: r.employee_id,
          completed_hours: r.completed_hours === '' ? null : Number(r.completed_hours),
          score: r.score === '' ? null : Number(r.score),
          status: r.status || 'pending',
          completed_at: r.completed_at || null
        }))
      };
      await http.put(`/education-training/training-plans/${activePlanId}/participants`, payload);
      addToast?.('岗中台账已保存', 'success');
      await fetchParticipants(activePlanId);
    } catch (err) {
      console.error('[onjob-ledger-tab] save failed:', err);
      addToast?.(err.response?.data?.message || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 style={sectionTitleStyle}>岗中教育培训台账（按培训计划主题）</h2>

      <div style={actionsBarStyle}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: '#64748b' }}>选择计划</div>
          <select
            value={activePlanId}
            onChange={(e) => setActivePlanId(e.target.value)}
            style={{ ...inputStyle, width: 360, background: '#fff' }}
            disabled={plansLoading}
          >
            {plans.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.topic}
              </option>
            ))}
          </select>
          <button style={btn('secondary')} onClick={fetchPlans} disabled={plansLoading}>
            {plansLoading ? '刷新中...' : '刷新计划'}
          </button>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button style={btn('secondary')} onClick={markAllCompletedToday} disabled={participantsLoading || participants.length === 0}>
            一键标记完成(今日)
          </button>
          <button style={btn('primary')} onClick={save} disabled={saving || participantsLoading || !activePlanId}>
            {saving ? '保存中...' : '保存登记'}
          </button>
        </div>
      </div>

      {activePlan ? (
        <div style={{ marginBottom: 10, fontSize: 12, color: '#64748b' }}>
          时间：{toDateRangeLabel(activePlan)} · 学时：{activePlan.training_hours ?? '—'} · 参培：{activePlan.participant_count ?? 0} 人 · 已完成：{activePlan.completed_count ?? 0} 人
        </div>
      ) : null}

      {participantsLoading ? (
        <div>正在加载参培人员...</div>
      ) : !activePlanId ? (
        <div style={{ color: '#64748b' }}>暂无可用培训计划</div>
      ) : participants.length === 0 ? (
        <div style={{ background: '#f8fafc', border: '1px dashed var(--border-color)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#475569' }}>
          该计划尚未关联参培员工。请先到“培训计划”里选择参培人员。
        </div>
      ) : (
        <div style={{ overflow: 'auto', background: '#fff', border: '1px solid var(--border-color)', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>姓名</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>部门</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>岗位</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>应训学时</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>登记学时</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>分次记录</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>考核成绩</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>状态</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>完成日期</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((row) => (
                <tr key={row.id}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.employee_name || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.department || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.position || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.planned_hours ?? '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 140 }}>
                    <input
                      type="number"
                      step="0.5"
                      value={row.completed_hours}
                      onChange={(e) => handleRowChange(row.id, 'completed_hours', e.target.value)}
                      style={inputStyle}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {Number(row.session_count || 0) > 0
                          ? `已登记 ${row.session_count} 次 · 已签 ${Number(row.signed_session_count || 0)} 次`
                          : '未登记'}
                      </div>
                      <button
                        type="button"
                        style={btn('secondary')}
                        onClick={() => openSessionsModal(row)}
                      >
                        分次登记
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 140 }}>
                    <input
                      type="number"
                      step="0.5"
                      value={row.score}
                      onChange={(e) => handleRowChange(row.id, 'score', e.target.value)}
                      style={inputStyle}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 160 }}>
                    <select
                      value={row.status || 'pending'}
                      onChange={(e) => handleRowChange(row.id, 'status', e.target.value)}
                      style={{ ...inputStyle, background: '#fff' }}
                    >
                      <option value="pending">未完成</option>
                      <option value="completed">已完成</option>
                    </select>
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 170 }}>
                    <input
                      type="date"
                      value={row.completed_at}
                      onChange={(e) => handleRowChange(row.id, 'completed_at', e.target.value)}
                      style={inputStyle}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <WideModal
        isOpen={sessionsOpen}
        onClose={() => {
          if (sessionsSaving) return;
          setSessionsOpen(false);
          setSessionsEmployee(null);
          setSessions([]);
        }}
        title={`分次学时登记：${sessionsEmployee?.employee_name || '员工'}（${statusLabel(sessionsEmployee?.status)}）`}
        size="lg"
        bodyStyle={{ maxHeight: '70vh' }}
      >
        {sessionsLoading ? (
          <div>正在加载...</div>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <button type="button" style={btn('secondary')} onClick={addSessionRow}>
                新增一次记录
              </button>
              <div style={{ fontSize: 12, color: '#64748b' }}>累计学时：{sessionsTotalHours}</div>
              <div style={{ flex: 1 }} />
              <button type="button" style={btn('primary')} onClick={saveSessions} disabled={sessionsSaving}>
                {sessionsSaving ? '保存中...' : '保存分次记录'}
              </button>
            </div>

            {sessions.length === 0 ? (
              <div style={{ background: '#f8fafc', border: '1px dashed var(--border-color)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#475569' }}>
                暂无分次记录，可点击“新增一次记录”。
              </div>
            ) : (
              <div style={{ overflow: 'auto', background: '#fff', border: '1px solid var(--border-color)', borderRadius: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>日期</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>学时</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>备注</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>签名</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s) => (
                      <tr key={s.id}>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 170 }}>
                          <input
                            type="date"
                            value={s.session_date}
                            onChange={(e) => updateSessionRow(s.id, 'session_date', e.target.value)}
                            style={inputStyle}
                          />
                        </td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 160 }}>
                          <input
                            type="number"
                            step="0.5"
                            value={s.hours}
                            onChange={(e) => updateSessionRow(s.id, 'hours', e.target.value)}
                            style={inputStyle}
                          />
                        </td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>
                          <input
                            type="text"
                            value={s.remark}
                            onChange={(e) => updateSessionRow(s.id, 'remark', e.target.value)}
                            placeholder="可选"
                            style={inputStyle}
                          />
                        </td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 240 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            {s.signature_data ? (
                              <img
                                src={s.signature_data}
                                alt="signature"
                                style={{ height: 30, maxWidth: 110, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: 4 }}
                              />
                            ) : (
                              <span style={{ fontSize: 12, color: '#64748b' }}>未签名</span>
                            )}
                            <span style={{ fontSize: 12, color: '#64748b' }}>{s.signer_name || ''}</span>
                            {s.signed_at ? (
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>{String(s.signed_at).slice(0, 19).replace('T', ' ')}</span>
                            ) : null}
                          </div>
                        </td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 220 }}>
                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              style={btn('secondary')}
                              onClick={() => openSessionSign(s)}
                              disabled={!isNumericId(s.id)}
                            >
                              {s.signature_data ? '重签' : '签名'}
                            </button>
                            <button type="button" style={btn('secondary')} onClick={() => removeSessionRow(s.id)}>
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: 10, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
              提示：这里记录的是“学时分次达成”明细；保存后会自动汇总到该员工的“登记学时”。
            </div>
          </div>
        )}
      </WideModal>

      <WideModal
        isOpen={sessionSignOpen}
        onClose={() => {
          if (sessionSignSaving) return;
          setSessionSignOpen(false);
          setSessionSigningRow(null);
        }}
        title={`场次签名：${sessionsEmployee?.employee_name || '员工'} · ${sessionSigningRow?.session_date || ''}`}
        size="md"
        bodyStyle={{ maxHeight: '75vh' }}
      >
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
          请在下方签名区域手写签名（手机端支持手指触控）。
        </div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 8, background: '#fff' }}>
          <canvas
            ref={sessionCanvasRef}
            onMouseDown={startSessionDraw}
            onMouseMove={moveSessionDraw}
            onMouseUp={endSessionDraw}
            onMouseLeave={endSessionDraw}
            onTouchStart={startSessionDraw}
            onTouchMove={moveSessionDraw}
            onTouchEnd={endSessionDraw}
            style={{ touchAction: 'none', display: 'block', width: '100%', height: 220, borderRadius: 8 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
          <button type="button" onClick={clearSessionCanvas} disabled={sessionSignSaving} style={btn('secondary')}>
            清空
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={saveSessionSignature} disabled={sessionSignSaving} style={btn('primary')}>
            {sessionSignSaving ? '保存中...' : '保存签名'}
          </button>
        </div>
      </WideModal>

      <p style={hintStyle}>说明：这里登记的是“培训计划-员工”维度的岗中学时与成绩，不影响现有的“安全培训台账（三级岗前/岗中）”功能。</p>
    </div>
  );
}
